<!-- Sidebar Navigation Component -->
<script setup lang="ts">
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import Button from '@/components/ui/Button.vue'

const router = useRouter()
const route = useRoute()
const uiStore = useUiStore()
const authStore = useAuthStore()

const navigation = [
  { name: 'Dashboard', icon: 'dashboard', path: '/' },
  { name: 'Devices', icon: 'devices', path: '/devices' },
  { name: 'History', icon: 'history', path: '/history' },
  { name: 'Settings', icon: 'settings', path: '/settings' },
] as const

const navClasses = computed(() => [
  'sidebar',
  { 'sidebar--collapsed': uiStore.sidebarCollapsed, 'sidebar--open': uiStore.sidebarOpen },
].filter(Boolean).join(' '))

function navigate(path: string) {
  router.push(path)
  // Close sidebar on mobile
  if (window.innerWidth < 1024) {
    uiStore.setSidebarOpen(false)
  }
}

function handleLogout() {
  authStore.logout()
  router.push('/login')
}
</script>

<template>
  <aside :class="navClasses" :aria-label="uiStore.sidebarCollapsed ? 'Navegación colapsada' : 'Navegación principal'" role="navigation">
    <div class="sidebar__header">
      <router-link to="/" class="sidebar__brand" @click.native.prevent="navigate('/')">
        <svg class="sidebar__logo" viewBox="0 0 32 32" fill="none" aria-hidden="true">
          <rect width="32" height="32" rx="8" fill="url(#grad)" />
          <path d="M8 20c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke="white" stroke-width="2" fill="none" stroke-linecap="round" />
          <path d="M16 12v8" stroke="white" stroke-width="2" stroke-linecap="round" />
          <circle cx="16" cy="20" r="2" fill="white" />
        </svg>
        <defs>
          <linearGradient id="grad" x1="0" y1="0" x2="32" y2="32">
            <stop offset="0%" stop-color="var(--color-brand-primary)" />
            <stop offset="100%" stop-color="var(--color-brand-secondary)" />
          </linearGradient>
        </defs>
        <span v-if="!uiStore.sidebarCollapsed" class="sidebar__title">Jardinera</span>
      </router-link>
      
      <button
        v-if="!uiStore.sidebarCollapsed"
        type="button"
        class="sidebar__toggle"
        @click="uiStore.toggleSidebarCollapsed"
        :aria-label="uiStore.sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'"
        :aria-expanded="!uiStore.sidebarCollapsed"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <polyline :points="uiStore.sidebarCollapsed ? '9 18 15 12 9 6' : '15 18 9 12 15 6'" />
        </svg>
      </button>
    </div>
    
    <nav class="sidebar__nav" aria-label="Menú principal">
      <ul class="sidebar__list" role="list">
        <li v-for="item in navigation" :key="item.name" class="sidebar__item">
          <button
            type="button"
            class="sidebar__link"
            :class="{ 'sidebar__link--active': route.path === item.path || (item.path !== '/' && route.path.startsWith(item.path)) }"
            @click="navigate(item.path)"
            :aria-current="route.path === item.path || (item.path !== '/' && route.path.startsWith(item.path)) ? 'page' : undefined"
          >
            <span class="sidebar__icon" aria-hidden="true">
              <component :is="`SidebarIcon${item.name}`" />
            </span>
            <span v-if="!uiStore.sidebarCollapsed" class="sidebar__label">{{ item.name }}</span>
          </button>
        </li>
      </ul>
    </nav>
    
    <div v-if="!uiStore.sidebarCollapsed" class="sidebar__footer">
      <div class="sidebar__user">
        <div class="sidebar__avatar" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>
        <div class="sidebar__user-info">
          <span class="sidebar__user-name">{{ authStore.user?.username || 'Usuario' }}</span>
          <span class="sidebar__user-role">{{ authStore.user?.role || 'user' }}</span>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        fullWidth
        @click="handleLogout"
      >
        <svg class="sidebar__logout-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
        Cerrar sesión
      </Button>
    </div>
  </aside>
  
  <!-- Mobile Overlay -->
  <div
    v-if="uiStore.sidebarOpen"
    class="sidebar__overlay"
    @click="uiStore.setSidebarOpen(false)"
    aria-hidden="true"
  />
</template>

<style scoped>
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  background: var(--color-bg-card);
  border-right: 1px solid var(--color-border-subtle);
  display: flex;
  flex-direction: column;
  z-index: var(--z-fixed);
  transition: width var(--transition-normal), transform var(--transition-normal);
  overflow: hidden;
  contain: layout style;
}

.sidebar--collapsed {
  width: var(--sidebar-collapsed-width);
}

.sidebar:not(.sidebar--collapsed) {
  width: var(--sidebar-width);
}

/* Mobile */
@media (max-width: 1023px) {
  .sidebar {
    transform: translateX(-100%);
    width: var(--sidebar-width);
  }
  
  .sidebar--open {
    transform: translateX(0) !important;
  }
  
  .sidebar--collapsed {
    transform: translateX(-100%);
  }
  
  .sidebar__overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: calc(var(--z-fixed) - 1);
  }
}

.sidebar__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-md);
  border-bottom: 1px solid var(--color-border-subtle);
  flex-shrink: 0;
  min-height: var(--header-height);
}

.sidebar__brand {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  color: var(--color-text-primary);
  text-decoration: none;
  flex: 1;
  min-width: 0;
}

.sidebar__logo {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.sidebar__title {
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  white-space: nowrap;
  overflow: hidden;
  transition: opacity var(--transition-fast), width var(--transition-fast);
}

.sidebar--collapsed .sidebar__title {
  opacity: 0;
  width: 0;
}

.sidebar__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.sidebar__toggle:hover {
  background: var(--color-bg-hover);
  color: var(--color-text-primary);
}

.sidebar__toggle svg {
  width: 20px;
  height: 20px;
}

.sidebar__nav {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-md) var(--space-sm);
  min-height: 0; /* Prevent flex item from overflowing */
}

.sidebar__list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}

.sidebar__link {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  width: 100%;
  padding: var(--space-sm) var(--space-md);
  border: none;
  background: transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
  text-align: left;
  white-space: nowrap;
}

.sidebar__link:hover {
  background: var(--color-bg-hover);
  color: var(--color-text-primary);
}

.sidebar__link--active {
  background: var(--color-brand-primary-light);
  color: var(--color-brand-primary);
}

.sidebar__link:focus-visible {
  outline: 2px solid var(--color-brand-primary);
  outline-offset: -2px;
}

.sidebar__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: currentColor;
}

.sidebar__icon svg {
  width: 24px;
  height: 24px;
}

.sidebar__label {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  overflow: hidden;
  transition: opacity var(--transition-fast), width var(--transition-fast);
}

.sidebar--collapsed .sidebar__label {
  opacity: 0;
  width: 0;
}

.sidebar__footer {
  padding: var(--space-md);
  border-top: 1px solid var(--color-border-subtle);
  flex-shrink: 0;
}

.sidebar__user {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm);
  margin-bottom: var(--space-sm);
}

.sidebar__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--color-brand-primary-light);
  border-radius: var(--radius-full);
  color: var(--color-brand-primary);
  flex-shrink: 0;
}

.sidebar__avatar svg {
  width: 20px;
  height: 20px;
}

.sidebar__user-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.sidebar__user-name {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar__user-role {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  text-transform: capitalize;
}

.sidebar__logout-icon {
  width: 18px;
  height: 18px;
  margin-right: var(--space-sm);
  flex-shrink: 0;
}
</style>